import {
    Component, Input, Output, EventEmitter, AfterViewInit, ElementRef, ViewChild,
    HostListener
} from '@angular/core';

export type RGB = { r: number; g: number; b: number };
export type HSV = { h: number; s: number; v: number };

@Component({
    selector: 'app-colorpicker',
    templateUrl: './colorpicker.component.html',
    styleUrls: ['./colorpicker.component.scss'],
    standalone: false
})
export class ColorpickerComponent implements AfterViewInit {
    @Input() color: string = '#FFFFFF';
    @Input() alpha: number | null = 1;
    @Input() inline: boolean = false;
    @Output() colorChange = new EventEmitter<string>();
    @Output() alphaChange = new EventEmitter<number>();

    @ViewChild('pickerBox') pickerBox!: ElementRef<HTMLDivElement>;
    @ViewChild('colorDisplay') colorDisplay!: ElementRef<HTMLDivElement>;
    @ViewChild('colorWheel') colorWheel!: ElementRef<HTMLCanvasElement>;
    @ViewChild('valueSlider') valueSlider!: ElementRef<HTMLDivElement>;
    @ViewChild('valueSliderHandle') valueSliderHandle!: ElementRef<SVGElement>;
    @ViewChild('alphaSlider') alphaSlider!: ElementRef<HTMLDivElement>;
    @ViewChild('alphaSliderHandle') alphaSliderHandle!: ElementRef<SVGElement>;
    @ViewChild('wheelSelector') wheelSelector!: ElementRef<SVGElement>;
    @ViewChild('hexField') hexField!: ElementRef<HTMLInputElement>;
    @ViewChild('rField') rField!: ElementRef<HTMLInputElement>;
    @ViewChild('gField') gField!: ElementRef<HTMLInputElement>;
    @ViewChild('bField') bField!: ElementRef<HTMLInputElement>;

    @HostListener('document:keydown.escape')
    onEscape() {
        if (!this.inline && this.popoverVisible) {
            this.popoverVisible = false;
        }
    };

    @HostListener('document:mousedown', ['$event'])
    onClickOutside(event: MouseEvent) {
        if (!this.inline && this.popoverVisible) {
            const clickedInside = this.elementRef.nativeElement.contains(event.target);
            if (!clickedInside) {
                this.popoverVisible = false;
            }
        }
    }

    currentHue = 0;
    currentSat = 0;
    currentVal = 1;
    currentAlpha = 1;
    popoverVisible = false;

    constructor(private elementRef: ElementRef) { }

    ngAfterViewInit(): void {
        [this.rField, this.gField, this.bField].forEach(field => {
            field.nativeElement.addEventListener('input', () => {
                const r = Math.min(255, Math.max(0, parseInt(this.rField.nativeElement.value) || 0));
                const g = Math.min(255, Math.max(0, parseInt(this.gField.nativeElement.value) || 0));
                const b = Math.min(255, Math.max(0, parseInt(this.bField.nativeElement.value) || 0));
                const { h, s, v } = this.rgbToHsv(r, g, b);
                this.currentHue = h;
                this.currentSat = s;
                this.currentVal = v;
                this.updateColor();
            });
        });

        this.hexField.nativeElement.addEventListener('input', () => {
            let hex = this.hexField.nativeElement.value.trim();
            if (!hex.startsWith('#')) hex = '#' + hex;
            if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
                const r = parseInt(hex.substring(1, 3), 16);
                const g = parseInt(hex.substring(3, 5), 16);
                const b = parseInt(hex.substring(5, 7), 16);
                const { h, s, v } = this.rgbToHsv(r, g, b);
                this.currentHue = h;
                this.currentSat = s;
                this.currentVal = v;
                this.updateColor();
            }
        });

        this.attachPointerEvents(this.colorWheel.nativeElement, this.handleWheelInteraction.bind(this));
        this.attachPointerEvents(this.valueSlider.nativeElement, (_, y) => this.handleValueSliderInteraction(y));
        this.attachPointerEvents(this.alphaSlider.nativeElement, (_, y) => this.handleAlphaSliderInteraction(y));
        this.drawColorWheel();
        this.setColor(this.color);
    }

    private setColor(hex: string) {
        if (hex.startsWith('0x')) hex = '#' + hex.slice(2);
        else if (!hex.startsWith('#')) hex = '#' + hex;

        if (hex.length === 4)
            hex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];

        let r = 0, g = 0, b = 0;

        if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
            r = parseInt(hex.substring(1, 3), 16);
            g = parseInt(hex.substring(3, 5), 16);
            b = parseInt(hex.substring(5, 7), 16);
        }

        const { h, s, v } = this.rgbToHsv(r, g, b);
        this.currentHue = h;
        this.currentSat = s;
        this.currentVal = v;

        this.updateColor();
    }

    private handleWheelInteraction(x: number, y: number) {
        const rect = this.colorWheel.nativeElement.getBoundingClientRect();
        const cx = this.colorWheel.nativeElement.getBoundingClientRect().width / 2;
        const cy = this.colorWheel.nativeElement.getBoundingClientRect().height / 2;
        const dx = x - rect.left - cx;
        const dy = y - rect.top - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        this.currentHue = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
        this.currentSat = Math.min(1, dist / (this.colorWheel.nativeElement.getBoundingClientRect().width / 2));
        this.updateColor();
    }

    private handleValueSliderInteraction(y: number) {
        const rect = this.valueSlider.nativeElement.getBoundingClientRect();
        this.currentVal = 1 - (Math.max(0, Math.min(rect.height, y - rect.top)) / rect.height);
        this.updateColor();
    }

    private handleAlphaSliderInteraction(y: number) {
        const rect = this.alphaSlider.nativeElement.getBoundingClientRect();
        this.currentAlpha = 1 - (Math.max(0, Math.min(rect.height, y - rect.top)) / rect.height);
        this.alphaChange.emit(this.currentAlpha);
        this.updateColor();
    }

    private updateColor() {
        const { r, g, b } = this.hsvToRgb(this.currentHue, this.currentSat, this.currentVal);
        const hexRGB = [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('').toUpperCase();
        const hex = `#${hexRGB}`;

        this.colorDisplay.nativeElement.style.backgroundColor = hex;
        this.hexField.nativeElement.value = hexRGB;
        this.rField.nativeElement.value = r.toString();
        this.gField.nativeElement.value = g.toString();
        this.bField.nativeElement.value = b.toString();

        const radius = (this.colorWheel.nativeElement.getBoundingClientRect().width / 2);
        const centerOffset = -(this.wheelSelector.nativeElement.getBoundingClientRect().width / 2);
        const x = radius + radius * this.currentSat * Math.cos(this.currentHue * Math.PI / 180) + centerOffset;
        const y = radius + radius * this.currentSat * Math.sin(this.currentHue * Math.PI / 180) + centerOffset;
        this.wheelSelector.nativeElement.style.left = `${x}px`;
        this.wheelSelector.nativeElement.style.top = `${y}px`;

        const sliderMin = 5;
        const valueSliderMax = this.valueSlider.nativeElement.clientHeight - 25;
        let valueSliderTop = (1 - this.currentVal) * this.valueSlider.nativeElement.clientHeight;
        valueSliderTop = Math.max(sliderMin, Math.min(valueSliderTop, valueSliderMax));
        this.valueSliderHandle.nativeElement.style.top = `${valueSliderTop}px`;
        this.valueSliderHandle.nativeElement.style.left = `calc(50% - 8px)`;
        const bright = this.hsvToRgb(this.currentHue, this.currentSat, 1);
        this.valueSlider.nativeElement.style.background = `linear-gradient(to top, rgb(0,0,0), rgb(${bright.r}, ${bright.g}, ${bright.b}))`;

        const alphaSliderMax = this.valueSlider.nativeElement.clientHeight - 25;
        let alphaSliderTop = (1 - this.currentAlpha) * this.alphaSlider.nativeElement.clientHeight;
        alphaSliderTop = Math.max(sliderMin, Math.min(alphaSliderTop, alphaSliderMax));
        this.alphaSliderHandle.nativeElement.style.top = `${alphaSliderTop}px`;
        this.alphaSliderHandle.nativeElement.style.left = `calc(50% - 8px)`;
        const color = this.hsvToRgb(this.currentHue, this.currentSat, this.currentVal);
        const checkerboard = `url("assets/checkerboard.svg")`
        this.alphaSlider.nativeElement.style.background = `linear-gradient(to top, rgba(${color.r}, ${color.g}, ${color.b}, 0), rgba(${color.r}, ${color.g}, ${color.b}, 1)), ${checkerboard}`;

        this.colorChange.emit(hex);
    }

    private drawColorWheel() {
        const ctx = this.colorWheel.nativeElement.getContext('2d')!;
        const radius = (this.colorWheel.nativeElement.width / 2);
        const image = ctx.createImageData(this.colorWheel.nativeElement.width, this.colorWheel.nativeElement.height);
        for (let y = 0; y < image.height + 0.5; y++) {
            for (let x = 0; x < image.width + 0.5; x++) {
                const dx = x - radius;
                const dy = y - radius;
                const dist = Math.sqrt(dx * dx + dy * dy);
                const angle = Math.atan2(dy, dx);
                const hue = (angle * 180 / Math.PI + 360) % 360;
                const sat = dist / radius;
                const { r, g, b } = this.hsvToRgb(hue, sat, 1);
                const i = (y * image.width + x) * 4;
                image.data[i] = r;
                image.data[i + 1] = g;
                image.data[i + 2] = b;
                image.data[i + 3] = 255;
            }
        }
        ctx.putImageData(image, 0, 0);
    }

    private hsvToRgb(h: number, s: number, v: number): RGB {
        const c = v * s;
        const x = c * (1 - Math.abs((h / 60) % 2 - 1));
        const m = v - c;
        let [r, g, b] = [0, 0, 0];
        if (h < 60) [r, g, b] = [c, x, 0];
        else if (h < 120) [r, g, b] = [x, c, 0];
        else if (h < 180) [r, g, b] = [0, c, x];
        else if (h < 240) [r, g, b] = [0, x, c];
        else if (h < 300) [r, g, b] = [x, 0, c];
        else[r, g, b] = [c, 0, x];
        return {
            r: Math.round((r + m) * 255),
            g: Math.round((g + m) * 255),
            b: Math.round((b + m) * 255)
        };
    }

    private rgbToHsv(r: number, g: number, b: number): HSV {
        r /= 255; g /= 255; b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        const d = max - min;
        let h = 0;
        if (d !== 0) {
            if (max === r) h = 60 * (((g - b) / d + 6) % 6);
            else if (max === g) h = 60 * ((b - r) / d + 2);
            else h = 60 * ((r - g) / d + 4);
        }
        const s = max === 0 ? 0 : d / max;
        return { h, s, v: max };
    }

    private attachPointerEvents(target: HTMLElement, moveFn: (x: number, y: number) => void) {
        const onMove = (e: MouseEvent | TouchEvent) => {
            const touch = (e as TouchEvent).touches?.[0];
            const x = touch ? touch.clientX : (e as MouseEvent).clientX;
            const y = touch ? touch.clientY : (e as MouseEvent).clientY;
            moveFn(x, y);
        };
        const onEnd = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('mouseup', onEnd);
            window.removeEventListener('touchend', onEnd);
        };
        target.addEventListener('mousedown', e => {
            onMove(e);
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onEnd);
        });
        target.addEventListener('touchstart', e => {
            onMove(e);
            window.addEventListener('touchmove', onMove);
            window.addEventListener('touchend', onEnd);
        }, { passive: false });
    }

    togglePopover() {
        this.popoverVisible = !this.popoverVisible;

        if (this.inline || !this.popoverVisible) return;

        requestAnimationFrame(() => {
            const picker = this.pickerBox?.nativeElement;
            const display = this.colorDisplay?.nativeElement;
            if (!picker || !display) return;

            const displayBox = display.getBoundingClientRect();
            const pickerWidth = picker.offsetWidth;
            const pickerHeight = picker.offsetHeight;
            let left = display.offsetLeft;
            let top = display.offsetTop + display.offsetHeight;
            const screenLeft = displayBox.left;
            const screenTop = displayBox.bottom;
            const overflowRight = screenLeft + pickerWidth > window.innerWidth;

            if (overflowRight) {
                const shiftLeft = screenLeft + pickerWidth - window.innerWidth + 8;
                left = Math.max(8, left - shiftLeft);
            }
            const overflowBottom = screenTop + pickerHeight > window.innerHeight;
            if (overflowBottom) {
                const shiftUp = screenTop + pickerHeight - window.innerHeight + 8;
                top = Math.max(8, top - pickerHeight - display.offsetHeight - shiftUp);
            }

            picker.style.position = 'absolute';
            picker.style.left = `${left + 2}px`;
            picker.style.top = `${top + 8}px`;

            this.updateColor();
        });
    }
}
